<template>
  <div>
    <h4 class="mb-3">{{ this.forTransfering ? $t("processing-transfering.reason-and-interest") : $t("processing.text") }}</h4>

    <h6 class="mt-3">
      <right-panel
        class="float-right"
        id="sidebar-new-processing-transfering-purpose"
        width="600px"
        :icon="'mdi mdi-plus'"
        :text="this.forTransfering ? $t('transfering.purpose.new') : $t('processing-transfering.purpose.new')"
        @hidden="rightPanelClosed"
      >
        <div class="pl-5 pr-5">
          <FormPurpose @onFormSave="onPurposeSelect" :hide-library="true" />
        </div>
      </right-panel>
      {{ this.forTransfering ? $t("transfering.purpose.text") : $t("processing.purpose.text") }}
    </h6>

    <filter-select
      id="purposeFilter"
      ref="purposeFilter"
      :placeholder="this.forTransfering ? $t('transfering.purpose.text') : $t('processing.purpose.text')"
      :multiple="true"
      @filter="onPurposeSearch"
      :selected="purposes"
      @changed="onPurposeSelect"
    />

    <h6 class="mt-3">
      <right-panel
        class="float-right"
        id="sidebar-new-processing-transfering-legal-reason"
        width="600px"
        :icon="'mdi mdi-plus'"
        :text="$t('processing-transfering.legal-reason.new')"
        @hidden="rightPanelClosed"
      >
        <div class="pl-5 pr-5">
          <FormLegalReason @onFormSave="onLegalReasonSelect" :hide-library="true" />
        </div>
      </right-panel>

      {{ $t("processing-transfering.legal-reason.text") }}
    </h6>
    <filter-select
      id="legalReasonFilter"
      ref="legalReasonFilter"
      :placeholder="$t('processing-transfering.legal-reason.text')"
      :multiple="true"
      @filter="onLegalReasonSearch"
      :selected="legalReasons"
      @changed="onLegalReasonSelect"
    />

    <sweet-button @click="prepare()" :disabled="purposes.length == 0 || legalReasons.length == 0" variant="secondary" icon="mdi mdi-plus" size="sm" buttonClass="mt-2 mr-2">
      {{ $t("common.add") }}
    </sweet-button>

    <table v-if="legals.length > 0" class="table mt-2">
      <tr class="bg-dark text-white">
        <th></th>
        <th style="width: 20%;">{{ $t("processing-transfering.purpose.text") }}</th>
        <th style="width: 20%;">{{ $t("common.aquivalent-verbis") }}</th>
        <th style="width: 25%;">{{ $t("processing-transfering.legal-reason.text") }}</th>
        <th style="width: 15%;">{{ $t("common.regulation") }}</th>
        <th style="width: 25%;">{{ $t("processing-transfering.legitimate-interest.text") }}</th>
      </tr>
      <tr v-for="(i, index) in legals" :key="i.legalReason.libraryItem._id + i.purpose.libraryItem._id">
        <td><i class="bx bx-trash text-danger hand" @click="removeItem(index)"></i></td>
        <td>{{ i.purpose.name | translate }}</td>
        <td>
          <span v-if="!i.purpose.isVerbisPurpose && i.purpose.verbisAquivalent">{{ i.purpose.verbisAquivalent.name | translate }}</span>
          <span v-else>{{ i.purpose.name | translate }}</span>
        </td>
        <td>{{ i.legalReason.name | translate }}</td>
        <td>{{ i.legalReason.regulation | translate }}</td>
        <td>
          <div class="d-flex">
            <i
              v-b-modal="id + i.legalReason.libraryItem._id + i.purpose.libraryItem._id + 'processing-x-definition-modal-select'"
              class="bx bx-search font-size-16 align-middle "
            ></i>
            <textarea
              @input="updateLegitimateInterest($event, i)"
              :value="i.legitimateInterest"
              :placeholder="$t('processing-transfering.legitimate-interest.info')"
              class="form-control form-control-sm"
              rows="4"
            ></textarea>
          </div>

          <ModalSelect
            :id="id + i.legalReason.libraryItem._id + i.purpose.libraryItem._id + 'processing-x-definition-modal-select'"
            :title="$t('processing-transfering.legitimate-interest.text')"
            store="legitimateInterest"
            @selected="legitimateInterestSelected($event, i)"
          />
        </td>
      </tr>
    </table>

    <transfering-legal-recommendation v-if="forTransfering" @onApply="applyRecommendation" />
    <processing-legal-recommendation v-else @onApply="applyRecommendation" />

    <sweet-button
      v-if="!$v.$invalid"
      @click="save()"
      :loading="isUpdating"
      :disabled="isUpdating || $v.$invalid"
      variant="success"
      icon="mdi mdi-check"
      size="sm"
      buttonClass="mt-2"
    >
      {{ $t("common.save") }}
    </sweet-button>

    <sweet-button @click="cancel()" variant="secondary" icon="mdi mdi-cancel" size="sm" buttonClass="mt-2 ml-2">
      {{ $t("common.cancel") }}
    </sweet-button>
  </div>
</template>

<script>
import ModalSelect from "@c/modal-select";
import FormLegalReason from "@modules/processing-transferring/workspace-library-legal-reason-form";
import FormPurpose from "@modules/processing-transferring/workspace-library-purpose-form";
import { required } from "vuelidate/lib/validators";
import ProcessingLegalRecommendation from "../recommendation/legal/processing-legal-recommendation";
import TransferingLegalRecommendation from "../recommendation/legal/transfering-legal-recommendation";

export default {
  props: ["prepareMode", "id", "forTransfering"],

  components: { ModalSelect, FormPurpose, FormLegalReason, TransferingLegalRecommendation, ProcessingLegalRecommendation },
  data() {
    return {
      legalReasons: [],
      purposes: [],
      legals: [],
    };
  },
  validations: {
    legals: { required },
  },
  computed: {
    isUpdating() {
      return this.$store.getters["datasubjectProcessingLegal/isUpdating"];
    },

    editedDatasubject() {
      return this.$store.getters["datasubjectProcessingLegal/editedDatasubject"];
    },
    editedDatasubjectProcessingLegal() {
      return this.$store.getters["datasubjectProcessingLegal/editedDatasubjectProcessingLegal"];
    },
  },
  watch: {
    editedDatasubjectProcessingLegal: {
      deep: true,
      immediate: true,
      handler(v) {
        if (v) {
          this.legals = [
            ...v.map((i) => ({
              ...i,
              legitimateInterestLocalized: i.legitimateInterest,
              legitimateInterest: i.legitimateInterest ? i.legitimateInterest[this.currentLanguage] : "",
            })),
          ];
        } else {
          this.legals = [];
        }
      },
    },
  },
  mounted() {},
  methods: {
    applyRecommendation(item) {
      this.legalReasons = item.legalReasons;
      this.purposes = item.purposes;
    },
    prepare() {
      this.legalReasons.forEach((legalReason) => {
        this.purposes.forEach((purpose) => {
          if (!this.legals.find((legal) => legal.purpose.libraryItem._id == purpose._id && legal.legalReason.libraryItem._id == legalReason._id)) {
            this.legals.push({
              legalReason: { ...legalReason, libraryItem: { _id: legalReason._id, name: legalReason.name } },
              purpose: { ...purpose, libraryItem: { _id: purpose._id, name: purpose.name } },
              legitimateInterest: "",
            });
          }
        });
      });

      this.purposes = [];
      this.legalReasons = [];
    },
    removeItem(index) {
      this.legals.splice(index, 1);
    },
    async save() {
      if (!this.prepareMode) {
        const ok = await this.confirm(this.$t("alert.approve"));
        if (!ok) {
          return;
        }
      }

      try {
        const item = {
          datasubject: this.editedDatasubject,
          items: this.legals.map((i) => ({ ...i, legitimateInterest: i.legitimateInterestLocalized, datasubject: this.editedDatasubject })),
        };

        if (this.prepareMode) {
          this.$emit("onSaved", item.items);
        } else {
          if (this.editedDatasubjectProcessingLegal) {
            item._id = this.editedDatasubjectProcessingLegal._id;
            await this.$store.dispatch("datasubjectProcessingLegal/update", item);
          } else {
            await this.$store.dispatch("datasubjectProcessingLegal/create", item);
          }
          this.$toast.success(this.$t("common.success"));
        }

        this.legals = [];
        this.$store.commit("datasubjectProcessingLegal/editedDatasubjectProcessingLegal", null);
        this.$store.commit("datasubjectProcessingLegal/editedDatasubject", null);

        this.$root.$emit("bv::toggle::collapse", this.id);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async onLegalReasonSearch(value, callback) {
      const response = await this.$store.dispatch(`legalReason/paginate`, {
        options: { limit: 1000 },
        query: { name: { value } },
      });

      callback(response.data?.docs ?? []);
    },
    onLegalReasonSelect(item) {
      if (!Array.isArray(item)) {
        item = [item, ...this.legalReasons];
      }

      this.legalReasons = item.map((i) => ({ name: i.name, regulation: i.regulation ?? this.regulationSelectorByProduct(i), _id: i._id }));
    },

    async onPurposeSearch(value, callback) {
      const response = await this.$store.dispatch(`purpose/paginate`, {
        options: { limit: 1000 },
        query: { name: { value } },
      });

      callback(response.data?.docs ?? []);
    },
    onPurposeSelect(item) {
      if (!Array.isArray(item)) {
        item = [item, ...this.purposes];
      }
      this.purposes = item.map((i) => ({ name: i.name, verbisAquivalent: i.verbisAquivalent, isVerbisPurpose: i.isVerbisPurpose, _id: i._id }));
    },

    updateLegitimateInterest($e, legal) {
      const legitimateInterest = $e.target.value;
      legal.legitimateInterest = legitimateInterest;
      legal.legitimateInterestLocalized = {
        tr: legitimateInterest,
        en: legitimateInterest,
        fr: legitimateInterest,
        de: legitimateInterest,
      };
    },

    legitimateInterestSelected(item, legal) {
      if (!item) {
        return;
      }
      legal.legitimateInterest = item.name[this.currentLanguage];
      legal.legitimateInterestLocalized = item.name;
    },

    cancel() {
      this.legals = [];
      this.$store.commit("datasubjectProcessingLegal/editedDatasubjectProcessingLegal", null);
      this.$store.commit("datasubjectProcessingLegal/editedDatasubject", null);

      this.$root.$emit("bv::toggle::collapse", this.id);
    },

    rightPanelClosed() {},
  },
};
</script>
<style scoped>
.card-body {
  padding: 2px !important;
}
.text-soft-primary {
  color: #94a5f8;
}
.card-header {
  color: #8e8e8e;
}
</style>
